import React, { useState } from 'react';
import {
  CButton,
  CFormGroup,
  CLabel,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CModalTitle,
} from '@coreui/react';
import _t from 'counterpart';
import { extractErrorMessage } from '../../helpers';
import { useMutation, useQuery } from 'react-query';
import { listSalesPeopleForLeads, getSalesStatuses, updateLeadsBulk } from '../../services/BackendService';
import QueryDataRenderer from '../../components/QueryDataRenderer';
import { ISalesStatus } from '../salesStatus/types';
import ButtonWithLoader from '../../components/ButtonWithLoader';
import { IBulkEditParamsLeads, ILeadsFiltersDTO, ISalesPerson } from './types';
import CustomSelect, { ISelectOption } from '../../components/CustomSelect';
import toast from 'react-hot-toast';

interface IProps {
  show: boolean;
  onClose: (refetch?: boolean) => void;
  leadsPageFilters: ILeadsFiltersDTO;
  leadsPageSearch: string | null | undefined;
  count: number;
  selectedLeadIds?: Set<string>;
}

const REMOVE_OPTION_VALUE = '__REMOVE__';

const BulkEditModal = ({ show, onClose, count, leadsPageFilters, leadsPageSearch, selectedLeadIds }: IProps) => {
  const selectedCount = selectedLeadIds ? selectedLeadIds.size : count;
  const [selectedStatus, setSelectedStatus] = useState<ISelectOption | null>();
  const [selectedSalesPerson, setSelectedSalesPerson] = useState<ISelectOption | null>();

  const resetStatus = () => {
    setSelectedStatus(null);
  };

  const resetSalesPerson = () => {
    setSelectedSalesPerson(null);
  };

  const handleStatusChange = (status: any) => {
    setSelectedStatus(status);
  };

  const handleSalesPersonChange = (sp: any) => {
    setSelectedSalesPerson(sp);
  };

  const updateLeadsMutation = useMutation((params: IBulkEditParamsLeads) => updateLeadsBulk(params), {
    onSuccess: () => {
      toast.success(_t('leads.leads-updated-successfully', { count: selectedCount }));
      resetStatus();
      resetSalesPerson();
      onClose(true);
    },
    onError: (e: any) => {
      const error = extractErrorMessage(e);
      toast.error(error);
    },
  });

  const salesStatusesQuery = useQuery('sales-statuses', () => getSalesStatuses(), {
    onError: (e: any) => {
      const error = extractErrorMessage(e);
      toast.error(error);
    },
  });

  const salesPeopleQuery = useQuery(['sales-people-bulk'], () => listSalesPeopleForLeads(), {
    onError: (e: any) => {
      const error = extractErrorMessage(e);
      toast.error(error);
    },
  });

  const isLoading = salesStatusesQuery.isLoading || salesPeopleQuery.isLoading;
  const isError = salesStatusesQuery.isError || salesPeopleQuery.isError;

  const handleOnRefetch = () => {
    salesStatusesQuery.refetch();
    salesPeopleQuery.refetch();
  };

  const statuses = salesStatusesQuery.data?.map((salesStatus: ISalesStatus) => {
    return {
      value: salesStatus.id,
      label: salesStatus.status,
    };
  });

  const salesPeopleOptions = [
    { value: REMOVE_OPTION_VALUE, label: _t('action.remove-assignee') },
    ...(salesPeopleQuery.data?.map((salesPerson: ISalesPerson) => ({
      value: salesPerson.id,
      label: `${salesPerson.firstname} ${salesPerson.lastname}`,
    })) || []),
  ];

  const closeHandler = () => {
    resetStatus();
    resetSalesPerson();
    onClose();
  };

  const invokeMutation = () => {
    const isRemovingSalesPerson = selectedSalesPerson?.value === REMOVE_OPTION_VALUE;

    let salesPersonValue: string | null | undefined = undefined;
    if (isRemovingSalesPerson) {
      salesPersonValue = null;
    } else if (selectedSalesPerson) {
      salesPersonValue = selectedSalesPerson.value;
    }

    updateLeadsMutation.mutate({
      ...leadsPageFilters,
      selectedStatus: selectedStatus ? Number(selectedStatus.value) : undefined,
      selectedSalesPerson: salesPersonValue,
      search: leadsPageSearch ?? undefined,
      leadIds: selectedLeadIds ? Array.from(selectedLeadIds) : undefined,
    });
  };

  return (
    <CModal show={show} onClose={closeHandler}>
      <CModalHeader>
        <CModalTitle>{_t('leads.bulk-edit-leads')}</CModalTitle>
      </CModalHeader>
      <CModalBody>
        <CLabel>{_t('leads.only-selected-attr')}</CLabel>
        <QueryDataRenderer isLoading={isLoading} isError={isError} onRefetch={handleOnRefetch}>
          <CFormGroup>
            <div className="d-flex align-items-center justify-content-between">
              <span>{_t('customers.filters.status')}</span>
              <CButton className="mb-0 p-0 text-danger reset-single" onClick={resetStatus}>
                {_t('action.reset').toUpperCase()}
              </CButton>
            </div>
            <CustomSelect
              value={selectedStatus ?? null}
              options={statuses}
              onChange={handleStatusChange}
              isClearable={false}
              whiteBackground
              placeholder={_t('leads.select-a-status')}
            />
          </CFormGroup>
          <CFormGroup>
            <div className="d-flex align-items-center justify-content-between">
              <span>{_t('leads.assignee')}</span>
              <CButton className="mb-0 p-0 text-danger reset-single" onClick={resetSalesPerson}>
                {_t('action.reset').toUpperCase()}
              </CButton>
            </div>
            <CustomSelect
              value={selectedSalesPerson ?? null}
              options={salesPeopleOptions}
              onChange={handleSalesPersonChange}
              isClearable={false}
              whiteBackground
              placeholder={_t('leads.select-an-assignee')}
            />
          </CFormGroup>
        </QueryDataRenderer>
      </CModalBody>
      <CModalFooter>
        <ButtonWithLoader
          isLoading={updateLeadsMutation.isLoading}
          onClick={invokeMutation}
          buttonColor="primary"
          spinnerColor="secondary"
          title={_t('leads.modify-leads', { count: selectedCount })}
          className="mr-2"
          disabled={selectedCount === 0}
        />
        <CButton color="light" variant="outline" onClick={closeHandler}>
          {_t('action.cancel')}
        </CButton>
      </CModalFooter>
    </CModal>
  );
};

export default React.memo(BulkEditModal);
